import { forwardRef, type HTMLAttributes, type RefObject } from 'react';

import { cn } from '@virginexperiencedays/tailwind';

export enum DataTestId {
  list = 'icon-list',
  listItem = 'icon-list-item',
  listIcon = 'icon-list-icon',
  listContent = 'icon-list-content',
}

// This follows Composition Pattern + Atomic Design
// Usage Reference: See implementation in storybook template mocks

// element for the overall list
export const IconList = forwardRef(
  (
    {
      className,
      children,
      ...rest
    }: HTMLAttributes<HTMLUListElement> & {
      isColumn?: boolean;
    },
    ref: RefObject<HTMLUListElement>
  ) => (
    <ul
      ref={ref}
      data-testid={DataTestId.list}
      className={cn('flex list-none flex-col items-start gap-6', className)}
      {...rest}
    >
      {children}
    </ul>
  )
);
IconList.displayName = 'IconList';

// element for each list item
export const IconListItem = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLLIElement> & {
  isColumn?: boolean;
}) => (
  <li
    data-testid={DataTestId.listItem}
    className={cn('flex list-none flex-row items-start gap-4', className)}
    {...rest}
  >
    {children}
  </li>
);

// element for icon wrapper
export const IconListIcon = ({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid={DataTestId.listIcon}
    className={cn('text-neutral-strong flex w-5 justify-center', className)}
    {...rest}
  >
    {children}
  </div>
);

// element for the styled content
export const IconListContent = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid={DataTestId.listContent}
    className={cn(
      'text-neutral-strong flex-1 text-left text-base font-[500] leading-normal',
      className
    )}
    {...rest}
  >
    {children}
  </div>
);
